import React, { ReactElement, useEffect, useMemo } from 'react';
import { Backdrop, Box, CircularProgress, Grid2, IconButton, Tooltip, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { RootState } from '../../redux/reducers';
import { importOrders, refreshOrdersData } from '../../redux/actions/orderActions';
import { importDemandOrders, refreshDemandOrdersData } from '../../redux/actions/demandOrderActions';
import { importReturnOrders, refreshReturnOrdersData } from '../../redux/actions/returnOrderActions';
import { importSites } from '../../redux/actions/siteActions';
import { importClients } from '../../redux/actions/clientActions';
import { importAllGoods } from '../../redux/actions/goodsActions';
import {
    isEmployeeProjectManager,
    isEmployeeWarehouseManager,
    isEmployeeWarehouseOwner,
} from '../../redux/selectors/employeeSelector';
import OrdersTable from '../orders/OrdersTable';
import { DetailsContainerBox, DetailsTableContainerBox } from '../../common/ResponsiveStyle';
import { TaskContext, isDemandOrderWaitingForMe, isOrderWaitingForMe, isReturnOrderWaitingForMe } from './myTasks';

/**
 * Početni ekran za ljude na terenu: samo nalozi, trebovanja i povratnice na kojima je sledeći
 * potez ulogovanog korisnika. Redovi su isti kao u listi naloga, pa i akcije rade isto.
 */
export default function MyTasksPage(): ReactElement {
    const dispatch = useAppDispatch();

    const orders = useAppSelector((state: RootState) => state.orders.orders);
    const demandOrders = useAppSelector((state: RootState) => state.demandOrders.demandOrders);
    const returnOrders = useAppSelector((state: RootState) => state.returnOrders.returnOrders);
    const sites = useAppSelector((state: RootState) => state.sites.sites);
    const clients = useAppSelector((state: RootState) => state.clients.clients);
    const goods = useAppSelector((state: RootState) => state.goods.goods);
    const employeeId = useAppSelector((state: RootState) => state.loggedEmployee.basicEmployee?.id);
    const isProjectManager = useAppSelector(isEmployeeProjectManager);
    const isWarehouseManager = useAppSelector(isEmployeeWarehouseManager);
    const isWarehouseOwner = useAppSelector(isEmployeeWarehouseOwner);

    useEffect(() => {
        if (!orders) {
            dispatch(importOrders());
        }
        if (!demandOrders) {
            dispatch(importDemandOrders());
        }
        if (!returnOrders) {
            dispatch(importReturnOrders());
        }
        if (!sites) {
            dispatch(importSites());
        }
        if (!clients) {
            dispatch(importClients());
        }
        if (!goods) {
            dispatch(importAllGoods());
        }
    }, [dispatch]);

    useEffect(() => {
        const interval = setInterval(() => {
            dispatch(refreshOrdersData());
            dispatch(refreshDemandOrdersData());
            dispatch(refreshReturnOrdersData());
        }, 60 * 1000);

        return () => clearInterval(interval);
    }, [dispatch]);

    const handleRefresh = () => {
        dispatch(importOrders());
        dispatch(importDemandOrders());
        dispatch(importReturnOrders());
    };

    const ctx: TaskContext = useMemo(
        () => ({ employeeId, isProjectManager, isWarehouseManager, isWarehouseOwner, sites }),
        [employeeId, isProjectManager, isWarehouseManager, isWarehouseOwner, sites],
    );

    const myOrders = useMemo(() => (orders ?? []).filter((o) => isOrderWaitingForMe(o, ctx)), [orders, ctx]);
    const myDemandOrders = useMemo(
        () => (demandOrders ?? []).filter((o) => isDemandOrderWaitingForMe(o, ctx)),
        [demandOrders, ctx],
    );
    const myReturnOrders = useMemo(
        () => (returnOrders ?? []).filter((o) => isReturnOrderWaitingForMe(o, ctx)),
        [returnOrders, ctx],
    );

    const isLoading = orders === null || demandOrders === null || returnOrders === null || sites === null;
    const total = myOrders.length + myDemandOrders.length + myReturnOrders.length;

    return isLoading ? (
        <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open>
            <CircularProgress color="inherit" />
        </Backdrop>
    ) : (
        <DetailsContainerBox
            sx={{
                p: 2,
                backgroundColor: (theme) => (theme.palette.mode === 'light' ? 'white' : theme.palette.grey[900]),
            }}
        >
            <Grid2 container sx={{ py: 2, px: { xs: 2, sm: 4 }, justifyContent: 'space-between', gap: 2 }}>
                <Grid2>
                    <Typography variant="h5">Moji zadaci</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {total === 0
                            ? 'Ništa ne čeka vaš potez.'
                            : total === 1
                              ? '1 stavka čeka vaš potez.'
                              : `${total} stavki čeka vaš potez.`}
                    </Typography>
                </Grid2>
                <Grid2>
                    <Tooltip title="Osveži">
                        <IconButton color="primary" onClick={handleRefresh} aria-label="Osveži">
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Grid2>
            </Grid2>
            <hr />
            <Box sx={{ p: { xs: 1, sm: 4 }, pt: 2 }}>
                {total === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 6, color: '#aaa' }}>
                        <Typography variant="h6">Sve je urađeno</Typography>
                        <Typography variant="body2">Kad neki nalog bude čekao vas, pojaviće se ovde.</Typography>
                    </Box>
                ) : (
                    <DetailsTableContainerBox>
                        <OrdersTable orders={myOrders} demandOrders={myDemandOrders} returnOrders={myReturnOrders} />
                    </DetailsTableContainerBox>
                )}
            </Box>
        </DetailsContainerBox>
    );
}
